import { Button, Input } from '@/components/ui';
import { Select, SelectOption } from '@/components/ui/Select';
import { Role, ROLES } from '@console/utils';
import { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { movideskApi, MovideskUser } from '@/lib/api/movidesk';
import { groupsApi } from '@/lib/api/groups';
import { isValidEmail } from '@console/utils';

const createUserSchema = (t: any) =>
  z.object({
    movideskUserId: z.string().optional(),
    name: z.string().min(1, t('users.nameRequired')),
    email: z.string().email(t('users.invalidEmail')).refine(isValidEmail, t('users.invalidEmail')),
    role: z.enum(['ADMIN', 'VIEWER', 'TIME_TRACKER'], {
      errorMap: () => ({ message: t('users.roleRequired') }),
    }),
    groupId: z.string().optional(),
  });

const updateUserSchema = (t: any) =>
  z.object({
    name: z.string().min(1, t('users.nameRequired')),
    email: z.string().email(t('users.invalidEmail')).refine(isValidEmail, t('users.invalidEmail')),
    role: z.enum(['ADMIN', 'VIEWER', 'TIME_TRACKER'], {
      errorMap: () => ({ message: t('users.roleRequired') }),
    }),
    groupId: z.string().optional(),
  });

export type CreateUserFormData = z.infer<ReturnType<typeof createUserSchema>>;
export type UpdateUserFormData = z.infer<ReturnType<typeof updateUserSchema>>;

export interface CreateUserFormProps {
  mode: 'create';
  onSubmit: (data: CreateUserFormData) => Promise<void>;
  isLoading: boolean;
  onCancel: () => void;
}

export interface UpdateUserFormProps {
  mode: 'update';
  onSubmit: (data: UpdateUserFormData) => Promise<void>;
  isLoading: boolean;
  initialData: {
    name: string;
    email: string;
    role: Role;
    groupId?: string;
  };
  onCancel: () => void;
}

export type UserFormProps = CreateUserFormProps | UpdateUserFormProps;

export function UserForm(props: UserFormProps) {
  const { mode, onSubmit, isLoading, onCancel } = props;
  const initialData = props.mode === 'update' ? props.initialData : undefined;
  const [movideskUsers, setMovideskUsers] = useState<MovideskUser[]>([]);
  const [movideskUsersOptions, setMovideskUsersOptions] = useState<SelectOption[]>([]);
  const [loadingMovidesk, setLoadingMovidesk] = useState(false);
  const [groups, setGroups] = useState<Array<{ id: string; name: string }>>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);

  const { t } = useTranslation();

  const isCreate = mode === 'create';
  const schema = isCreate ? createUserSchema(t) : updateUserSchema(t);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    clearErrors,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: isCreate
      ? {
          movideskUserId: '',
          name: '',
          email: '',
          role: 'TIME_TRACKER' as Role,
          groupId: '',
        }
      : {
          name: initialData?.name || '',
          email: initialData?.email || '',
          role: initialData?.role || ('TIME_TRACKER' as Role),
          groupId: initialData?.groupId || '',
        },
  });

  const rolesOptions = useMemo<SelectOption[]>(
    () =>
      ROLES.map((role) => ({
        value: role,
        content: t(`common.roles.${role}`),
      })),
    [t]
  );

  const groupsOptions = useMemo<SelectOption[]>(
    () =>
      groups.map((group) => ({
        value: group.id,
        content: group.name,
      })),
    [groups]
  );

  useEffect(() => {
    fetchGroups();
    if (isCreate) {
      fetchMovideskUsers();
    }
  }, [isCreate]);

  useEffect(() => {
    if (initialData && !isCreate) {
      reset({
        name: initialData.name,
        email: initialData.email,
        role: initialData.role,
        groupId: initialData.groupId || '',
      });
    }
  }, [initialData, isCreate, reset]);

  async function fetchGroups() {
    setLoadingGroups(true);
    try {
      const fetchedGroups = await groupsApi.getAllForSelect();
      setGroups(fetchedGroups);
    } catch (error) {
      console.error('Failed to fetch groups:', error);
      toast.error(t('groups.loadError'));
      setGroups([]);
    } finally {
      setLoadingGroups(false);
    }
  }

  async function fetchMovideskUsers() {
    setLoadingMovidesk(true);
    try {
      const users = await movideskApi.getUsersForSelect();
      const options: SelectOption[] = users.map((user) => ({
        value: user.id,
        content: `${user.name} (${user.email})`,
      }));
      setMovideskUsersOptions(options);
      setMovideskUsers(users);
    } catch (error) {
      console.error('Failed to fetch Movidesk users:', error);
      toast.error(t('users.createModal.movideskFetchError'));
      setMovideskUsers([]);
    } finally {
      setLoadingMovidesk(false);
    }
  }

  function fillForm(movideskUserId: string) {
    const movideskUser = movideskUsers.find(({ id }) => id === movideskUserId);

    if (!movideskUser) return;

    setValue('name', movideskUser.name);
    setValue('email', movideskUser.email);
    setValue('role', movideskUser.userType === 3 ? 'ADMIN' : 'TIME_TRACKER');
    clearErrors();
  }

  const handleFormSubmit = async (data: any) => {
    await onSubmit(data);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="gap-md flex flex-col">
      {isCreate && (
        <>
          <Select
            label={t('users.createModal.movideskUser')}
            placeholder={t('users.createModal.movideskUserPlaceholder')}
            itemsLabel={t('users.createModal.movideskUserItemsLabel')}
            options={movideskUsersOptions}
            loading={loadingMovidesk}
            value={watch('movideskUserId')}
            onValueChange={(value) => {
              fillForm(value);
              setValue('movideskUserId', value);
            }}
            error={isCreate ? (errors as any).movideskUserId?.message : undefined}
            disabled={isLoading}
          />
        </>
      )}

      <Input
        {...register('name')}
        label={t('users.createModal.name')}
        placeholder={t('users.createModal.namePlaceholder')}
        error={errors.name?.message}
        disabled={isLoading}
      />

      <Input
        {...register('email')}
        label={t('users.createModal.email')}
        placeholder={t('users.createModal.emailPlaceholder')}
        type="email"
        error={errors.email?.message}
        disabled={isLoading}
      />

      <Select
        label={t('users.createModal.role')}
        placeholder={t('users.createModal.rolePlaceholder')}
        itemsLabel={t('users.createModal.roleItemsLabel')}
        options={rolesOptions}
        value={watch('role')}
        onValueChange={(value) => setValue('role', value as Role)}
        error={errors.role?.message}
        disabled={isLoading}
      />

      <Select
        label={t('users.createModal.group')}
        placeholder={t('users.createModal.groupPlaceholder')}
        itemsLabel={t('users.createModal.groupItemsLabel')}
        options={groupsOptions}
        value={watch('groupId')}
        onValueChange={(value) => setValue('groupId', value)}
        error={errors.groupId?.message}
        disabled={isLoading}
        loading={loadingGroups}
      />

      <footer className="gap-sm mt-2xl flex self-end">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={isLoading}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" loading={isLoading} disabled={loadingMovidesk && isCreate}>
          {t(isCreate ? 'common.create' : 'common.save')}
        </Button>
      </footer>
    </form>
  );
}
